import Catalog from 'files/Catalog.js';

/*
 * Quote
 *
 * The visitor's list of what they want quoted. Mirrors the shape of the
 * storefront's classes/Cart.js — init, mutate, subscribe — so the drawer that
 * was ported from it did not need rewiring, but it is a local list rather than
 * a checkout: nothing is reserved, nothing is priced, and no server sees it
 * until the request is actually submitted.
 *
 * Scoped per dealership, which matters more than it first appears. Several
 * dealerships are served from the same origin as a path — homesafealerts.com/
 * dealer-a and /dealer-b share one localStorage — so an unscoped key would let
 * one dealer's quote follow the visitor to another dealer's website.
 *
 * Only { handle, quantity } is persisted. Titles, images and copy are looked
 * up from the catalogue on read, so editing product copy is never frozen into
 * whatever happened to be in a visitor's browser three months ago. Handles the
 * current catalogue no longer contains are dropped on read, which is what
 * makes a dealership switching to the water-only range safe.
 */

const STORAGE_PREFIX = 'replicated_quote';
const STORAGE_VERSION = 1;

let dealershipID = null;
let listeners = [];
let mode = 'full';
let state = { items: [], quiz: null };

const getStorageKey = () => `${STORAGE_PREFIX}_${dealershipID || 'unknown'}`;

const notify = () => {
    let current = Quote.get();
    listeners.forEach(listener => {
        try {
            listener(current);
        } catch(e) {
            console.error(`Quote listener failed: ${e.message}`);
        }
    });
}

const persist = () => {
    try {
        window.localStorage.setItem(getStorageKey(), JSON.stringify({
            items: state.items,
            quiz: state.quiz,
            v: STORAGE_VERSION
        }));
    } catch(e) {
        // Safari in private mode throws on write. The quote still works for
        // the length of the visit; it just will not survive a reload.
        console.log(`Quote could not be saved: ${e.message}`);
    }
    notify();
}

const restore = () => {
    try {
        let raw = window.localStorage.getItem(getStorageKey());
        if(!raw) {
            return { items: [], quiz: null };
        }
        let parsed = JSON.parse(raw);
        if(parsed?.v !== STORAGE_VERSION || Array.isArray(parsed.items) === false) {
            return { items: [], quiz: null };
        }
        return {
            items: parsed.items.filter(item => item?.handle && parseInt(item.quantity) > 0).map(item => ({
                handle: item.handle,
                quantity: parseInt(item.quantity)
            })),
            quiz: parsed.quiz || null
        };
    } catch(e) {
        return { items: [], quiz: null };
    }
}

const Quote = {

    init: (id, catalogMode) => {
        dealershipID = id;
        mode = catalogMode || 'full';
        state = restore();
        notify();
        return Quote.get();
    },

    // Items are returned hydrated from the catalogue, with anything the
    // current catalogue does not carry silently dropped.
    get: () => {
        // Ordered by the catalogue, not by when things were added, so the quote
        // drawer, the request invoice and the walkthrough results all read the
        // same way. The catalogue is the one place that order is decided.
        let order = Catalog.getAll(mode).map(product => product.handle);
        let items = state.items.reduce((list, item) => {
            let product = Catalog.getByHandle(item.handle, mode);
            if(product) {
                list.push({ ...product, quantity: item.quantity });
            }
            return list;
        }, []).sort((a, b) => order.indexOf(a.handle) - order.indexOf(b.handle));
        return {
            count: items.reduce((total, item) => total + item.quantity, 0),
            items: items,
            quiz: state.quiz
        };
    },

    getItems: () => Quote.get().items,

    getItemCount: () => Quote.get().count,

    // Adding something already on the list increases its quantity rather than
    // repeating the line, which is what someone clicking "add" twice means.
    addItem: (handle, quantity = 1) => {
        if(!Catalog.getByHandle(handle, mode)) {
            return Quote.get();
        }
        let existing = state.items.find(item => item.handle === handle);
        if(existing) {
            existing.quantity += quantity;
        } else {
            state.items.push({ handle: handle, quantity: quantity });
        }
        persist();
        return Quote.get();
    },

    // Set a handle to an exact quantity, whether or not it is already on the
    // list. Applying a coverage plan uses this rather than addItem: the plan
    // describes what the home needs, so applying it twice has to leave the
    // same result as applying it once. With addItem, reopening a completed
    // walkthrough and pressing the button again silently doubled everything.
    setItem: (handle, quantity) => {
        if(!Catalog.getByHandle(handle, mode) || quantity <= 0) {
            return Quote.get();
        }
        let existing = state.items.find(item => item.handle === handle);
        if(existing) {
            existing.quantity = quantity;
        } else {
            state.items.push({ handle: handle, quantity: quantity });
        }
        persist();
        return Quote.get();
    },

    updateItem: (handle, quantity) => {
        if(quantity <= 0) {
            return Quote.removeItem(handle);
        }
        let existing = state.items.find(item => item.handle === handle);
        if(existing) {
            existing.quantity = quantity;
            persist();
        }
        return Quote.get();
    },

    removeItem: handle => {
        state.items = state.items.filter(item => item.handle !== handle);
        persist();
        return Quote.get();
    },

    // The coverage quiz result travels with the request so the dealership
    // arrives already knowing the house.
    setQuiz: result => {
        state.quiz = result || null;
        persist();
        return Quote.get();
    },

    clear: () => {
        state = { items: [], quiz: null };
        persist();
        return Quote.get();
    },

    subscribe: listener => {
        listeners.push(listener);
        return () => {
            let index = listeners.indexOf(listener);
            if(index >= 0) {
                listeners.splice(index, 1);
            }
        };
    }
}

export default Quote;
